import { ALL_FEEDSTOCK_KEYS, FEEDSTOCK_LABELS, FeedstockKey, MetricKey } from '../types/emission-factor.constants';
import { EmissionFactorRecord, FeedstockYearlyValue } from '../types/emission-factor.api';
import React, { useMemo, useState } from 'react';
import { Caption, SectionLabel, Text } from '../../../shared/styles/components/primitives.styles';

import Button from '@ingka/button';
import { IconButton } from '../../../shared/components/IconButton';
import { Input } from '../../../shared/styles/components/forms.styles';
import { Modal } from '../../../shared/styles/components/modal.styles';
import crossIcon from '@ingka/ssr-icon/paths/cross';
import { formatNumber } from '../../../shared/utils/formatters.utils';
import styled from '../../../shared/types/transient-styled.types';
import { theme } from '../../../shared/styles/core/theme';
import { toPercent } from '../utils/emission-factor.utils';
import { tokens } from '../../../shared/styles/core/tokens';

export interface ChangeItem {
    id: string;
    year: number;
    feedstock: FeedstockKey;
    feedstockLabel: string;
    field: MetricKey;
    fieldLabel: string;
    oldValue: number | null;
    newValue: number | null;
}

interface ReviewChangesModalProps {
    visible: boolean;
    changes: ChangeItem[];
    addedYears: number[];
    removedYears: number[];
    groupSiblings: string[];
    onConfirm: (comments: Record<string, string>, modifyAllInGroup: boolean) => void;
    onCancel: () => void;
    isSaving: boolean;
}

const ChangesList = styled.div`
   display: flex;
   flex-direction: column;
   gap: ${tokens.space.md};
   max-height: 400px;
   overflow-y: auto;
`;

const ChangeCard = styled.div`
   padding: ${tokens.space.md};
   background-color: ${theme.colors.bg.secondary};
   border: ${tokens.border.thin} ${theme.colors.border.default};
   border-radius: ${tokens.radius.md};
   display: flex;
   flex-direction: column;
   gap: ${tokens.space.xs};
`;

const ValueChange = styled.div`
   display: flex;
   align-items: center;
   gap: ${tokens.space.sm};
`;

const Strikethrough = styled(Text)`
   text-decoration: line-through;
`;

const ReviewDialogContainer = styled(Modal.Container)`
   max-width: 500px;
`;

const GroupToggleRow = styled.label`
   display: flex;
   align-items: flex-start;
   gap: ${tokens.space.sm};
   padding: ${tokens.space.sm} ${tokens.space.md};
   border: ${tokens.border.thin} ${theme.colors.border.default};
   border-radius: ${tokens.radius.md};
   background-color: ${theme.colors.bg.secondary};
   cursor: pointer;

   input[type='checkbox'] {
      margin-top: 2px;
      flex-shrink: 0;
      accent-color: ${theme.colors.border.focus};
      cursor: pointer;
   }
`;

const getValue = (feedstock: FeedstockYearlyValue | undefined, field: MetricKey): number | null =>
    feedstock?.[field] ?? null;

export const computeChanges = (
    original: EmissionFactorRecord | null,
    current: EmissionFactorRecord | null
): ChangeItem[] => {
    if (!original || !current) return [];

    const changes: ChangeItem[] = [];
    const fields: MetricKey[] = ['emission', 'share'];
    const originalByYear = new Map(original.yearlyValues.map((yv) => [yv.year, yv]));

    current.yearlyValues.forEach((currentYV) => {
        const originalYV = originalByYear.get(currentYV.year);

        ALL_FEEDSTOCK_KEYS.forEach((feedstockKey) => {
            const currentFeedstock = currentYV[feedstockKey];
            const originalFeedstock = originalYV?.[feedstockKey];

            fields.forEach((field) => {
                const oldValue = getValue(originalFeedstock, field);
                const newValue = getValue(currentFeedstock, field);

                if (oldValue !== newValue) {
                    changes.push({
                        id: `${currentYV.year}-${feedstockKey}-${field}`,
                        year: currentYV.year,
                        feedstock: feedstockKey,
                        feedstockLabel: FEEDSTOCK_LABELS[feedstockKey],
                        field,
                        fieldLabel: field === 'emission' ? 'Emission Factor' : 'Share (%)',
                        oldValue,
                        newValue,
                    });
                }
            });
        });
    });

    return changes;
};

export const ReviewChangesModal: React.FC<ReviewChangesModalProps> = ({
    visible,
    changes,
    addedYears,
    removedYears,
    groupSiblings,
    onConfirm,
    onCancel,
    isSaving,
}) => {
    const [comments, setComments] = useState<Record<string, string>>({});
    const [modifyAllInGroup, setModifyAllInGroup] = useState(false);

    React.useEffect(() => {
        if (visible) {
            setComments({});
            setModifyAllInGroup(false);
        }
    }, [visible]);

    const handleCommentChange = (changeId: string, value: string) =>
        setComments((prev) => ({ ...prev, [changeId]: value }));

    const handleConfirm = () => onConfirm(comments, modifyAllInGroup);

    const sortedAddedYears = useMemo(() => [...addedYears].sort((a, b) => a - b), [addedYears]);
    const sortedRemovedYears = useMemo(() => [...removedYears].sort((a, b) => a - b), [removedYears]);

    const changesByYear = useMemo(() => {
        const grouped: Record<string, ChangeItem[]> = {};
        changes.forEach((change) => {
            const key = String(change.year);
            (grouped[key] ??= []).push(change);
        });
        return grouped;
    }, [changes]);

    const totalCount = changes.length + addedYears.length + removedYears.length;

    if (!visible) return null;

    return (
        <Modal.Overlay onClick={onCancel}>
            <ReviewDialogContainer onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <Modal.Header>
                    <Modal.Title>Review Changes</Modal.Title>
                    <IconButton onClick={onCancel} icon={crossIcon} disabled={isSaving} title="Close" />
                </Modal.Header>
                <Modal.Content>
                    <Text color="secondary">
                        You've made {totalCount} change{totalCount !== 1 ? 's' : ''}.
                        {changes.length > 0 && ' Add optional comments to explain why.'}
                    </Text>
                    {groupSiblings.length > 0 && (
                        <GroupToggleRow>
                            <input
                                type="checkbox"
                                checked={modifyAllInGroup}
                                onChange={(e) => setModifyAllInGroup(e.target.checked)}
                            />
                            <div>
                                <Text weight="semibold">Apply to all grades in this group</Text>
                                <Caption style={{ display: 'block' }}>
                                    Also affects {groupSiblings.length} other grade{groupSiblings.length !== 1 ? 's' : ''}: {groupSiblings.join(', ')}
                                </Caption>
                            </div>
                        </GroupToggleRow>
                    )}
                    <ChangesList>
                        {sortedAddedYears.length > 0 && (
                            <>
                                <ChangeCard>
                                    <Text weight="semibold">
                                        New year{sortedAddedYears.length !== 1 ? 's' : ''} added — {sortedAddedYears.join(', ')}
                                    </Text>
                                </ChangeCard>
                                {sortedAddedYears.map((year) =>
                                    changesByYear[String(year)]?.length ? (
                                        <div key={`added-changes-${year}`}>
                                            <SectionLabel>{year}</SectionLabel>
                                            {changesByYear[String(year)].map((change) => (
                                                <ChangeCard key={change.id}>
                                                    <Text weight="semibold">
                                                        {change.feedstockLabel} - {change.fieldLabel}
                                                    </Text>
                                                    <ValueChange>
                                                        <Strikethrough color="tertiary">
                                                            {formatNumber(change.field === 'share' ? toPercent(change.oldValue) : change.oldValue)}
                                                        </Strikethrough>
                                                        <Text color="tertiary" aria-hidden="true">→</Text>
                                                        <Text color="success" weight="semibold">
                                                            {formatNumber(change.field === 'share' ? toPercent(change.newValue) : change.newValue)}
                                                        </Text>
                                                    </ValueChange>
                                                    <Input
                                                        type="text"
                                                        placeholder="Add comment (optional)"
                                                        value={comments[change.id] ?? ''}
                                                        onChange={(e) => handleCommentChange(change.id, e.target.value)}
                                                    />
                                                </ChangeCard>
                                            ))}
                                        </div>
                                    ) : null
                                )}
                            </>
                        )}
                        {sortedRemovedYears.length > 0 && (
                            <ChangeCard style={{ borderColor: theme.colors.state.error }}>
                                <Text weight="semibold" color="error">
                                    Year{sortedRemovedYears.length !== 1 ? 's' : ''} removed — {sortedRemovedYears.join(', ')}
                                </Text>
                            </ChangeCard>
                        )}
                        {Object.entries(changesByYear)
                            .filter(([year]) => !addedYears.includes(Number(year)))
                            .map(([year, yearChanges]) => (
                                <div key={year}>
                                    <SectionLabel>{year}</SectionLabel>
                                    {yearChanges.map((change) => (
                                        <ChangeCard key={change.id}>
                                            <Text weight="semibold">
                                                {change.feedstockLabel} - {change.fieldLabel}
                                            </Text>
                                            <ValueChange>
                                                <Strikethrough color="tertiary">
                                                    {formatNumber(change.field === 'share' ? toPercent(change.oldValue) : change.oldValue)}
                                                </Strikethrough>
                                                <Text color="tertiary" aria-hidden="true">→</Text>
                                                <Text color="success" weight="semibold">
                                                    {formatNumber(change.field === 'share' ? toPercent(change.newValue) : change.newValue)}
                                                </Text>
                                            </ValueChange>
                                            <Input
                                                type="text"
                                                placeholder="Add comment (optional)"
                                                value={comments[change.id] ?? ''}
                                                onChange={(e) => handleCommentChange(change.id, e.target.value)}
                                            />
                                        </ChangeCard>
                                    ))}
                                </div>
                            ))}
                    </ChangesList>
                </Modal.Content>
                <Modal.Footer>
                    <Button text="Cancel" type="secondary" onClick={onCancel} disabled={isSaving} small />
                    <Button
                        text={isSaving ? 'Saving...' : 'Save Changes'}
                        type="primary"
                        onClick={handleConfirm}
                        disabled={isSaving}
                        small
                    />
                </Modal.Footer>
            </ReviewDialogContainer>
        </Modal.Overlay>
    );
};

export default ReviewChangesModal;